import { useEffect, useState, type JSX } from "react";
import { toast } from "sonner";
import type { Day, MealType, Slot } from "@recipevault/shared";
import { MEAL_TYPE_RANK } from "@recipevault/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/ui/sheet.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import { useRecipes } from "../recipes/useRecipes.js";
import { friendlyError } from "../../shared/http.js";
import { MEAL_LABELS } from "./mealLabels.js";
import { resolveItemLabel } from "./resolveItem.js";
import {
  addRecipeToSlot,
  addFreetextToSlot,
  setSlotLeftover,
  setSlotLeftoverFromPosition,
  moveSlotItems,
  removeSlotItems,
  copySlotItemsToTargets,
} from "./editWeek.js";

type Mode = "menu" | "recipe" | "freetext" | "leftover" | "move" | "copy";

export interface SlotSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weekStart: string;
  date: string;
  mealType: MealType;
  slot: Slot;
  /** The whole week's days — source of leftover candidates + move targets (by position). */
  days: Day[];
  recipeTitles: Map<string, string>;
}

const MEAL_TYPE_ORDER: MealType[] = ["breakfast", "lunch", "dinner"];

/** True when (cDate, cMealType) is strictly chronologically before (eDate, eMealType). */
function isPriorSlot(
  cDate: string,
  cMealType: MealType,
  eDate: string,
  eMealType: MealType,
): boolean {
  if (cDate < eDate) return true;
  return cDate === eDate && MEAL_TYPE_RANK[cMealType] < MEAL_TYPE_RANK[eMealType];
}

/**
 * State-aware slot editor. Every mutation goes through `editWeek`'s helpers
 * (`addRecipeToSlot` / `addFreetextToSlot` / `setSlotLeftover(FromPosition)` /
 * `moveSlotItems` / `removeSlotItems`), which lazy-create the week and always resolve
 * targets — and any leftover *source* slot id — by (date, mealType) position against the
 * real Dexie row. This sheet itself never reads or writes a slot id directly.
 */
export function SlotSheet({
  open,
  onOpenChange,
  weekStart,
  date,
  mealType,
  slot,
  days,
  recipeTitles,
}: SlotSheetProps): JSX.Element {
  const [mode, setMode] = useState<Mode>("menu");
  const [busy, setBusy] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [recipeSearch, setRecipeSearch] = useState("");
  const [freetextTitle, setFreetextTitle] = useState("");
  const [freetextReference, setFreetextReference] = useState("");
  const [leftoverLabel, setLeftoverLabel] = useState("");
  const [copyTargets, setCopyTargets] = useState<Set<string>>(new Set());
  const { recipes } = useRecipes({ search: recipeSearch });

  const isEmpty = slot.items.length === 0 && slot.leftoverOf === undefined;

  // Reset transient UI state whenever the sheet closes, or is retargeted at a different
  // slot while still open (defensive — DaySection only opens one at a time in practice).
  useEffect(() => {
    setMode("menu");
    setBusy(false);
    setConfirmingRemove(false);
    setRecipeSearch("");
    setFreetextTitle("");
    setFreetextReference("");
    setLeftoverLabel("");
    setCopyTargets(new Set());
  }, [open, date, mealType]);

  function toggleCopyTarget(key: string): void {
    setCopyTargets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await action();
      onOpenChange(false);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  const leftoverCandidates = days
    .flatMap((d) => d.slots.map((s) => ({ date: d.date, slot: s })))
    .filter(
      (c) =>
        !(c.date === date && c.slot.mealType === mealType) &&
        c.slot.skipped !== true &&
        c.slot.items.length > 0 &&
        isPriorSlot(c.date, c.slot.mealType, date, mealType),
    );

  const moveTargets = days
    .flatMap((d) => MEAL_TYPE_ORDER.map((mt) => ({ date: d.date, mealType: mt })))
    .filter((t) => !(t.date === date && t.mealType === mealType));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {MEAL_LABELS[mealType]} &middot; {date}
          </SheetTitle>
          <SheetDescription>
            {isEmpty ? "Add something to this slot." : "Manage this slot."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-3 px-4 pb-4">
          {isEmpty ? (
            <>
              {mode === "menu" && (
                <div className="flex flex-col gap-2">
                  <Button variant="outline" className="justify-start" onClick={() => { setMode("recipe"); }}>
                    Pick a saved recipe
                  </Button>
                  <Button variant="outline" className="justify-start" onClick={() => { setMode("freetext"); }}>
                    Add a dish
                  </Button>
                  <Button variant="outline" className="justify-start" onClick={() => { setMode("leftover"); }}>
                    This is leftovers from&hellip;
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    disabled={busy}
                    onClick={() => {
                      void run(() => addFreetextToSlot(weekStart, date, mealType, "Eating out"));
                    }}
                  >
                    Eating out
                  </Button>
                </div>
              )}

              {mode === "recipe" && (
                <div className="flex flex-col gap-2">
                  <BackButton onClick={() => { setMode("menu"); }} />
                  <Input
                    aria-label="Search recipes"
                    placeholder="Search recipes&hellip;"
                    value={recipeSearch}
                    onChange={(e) => { setRecipeSearch(e.target.value); }}
                  />
                  <div className="flex flex-col gap-1 overflow-y-auto">
                    {recipes.length === 0 ? (
                      <p className="text-sm text-zinc-500">No matching recipes.</p>
                    ) : (
                      recipes.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            void run(() => addRecipeToSlot(weekStart, date, mealType, r.id));
                          }}
                          className="rounded-md px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                        >
                          {r.title}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {mode === "freetext" && (
                <div className="flex flex-col gap-3">
                  <BackButton onClick={() => { setMode("menu"); }} />
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="slot-freetext-title">Title</Label>
                    <Input
                      id="slot-freetext-title"
                      value={freetextTitle}
                      onChange={(e) => { setFreetextTitle(e.target.value); }}
                      placeholder="e.g. Pizza night"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="slot-freetext-reference">Reference (optional)</Label>
                    <Input
                      id="slot-freetext-reference"
                      value={freetextReference}
                      onChange={(e) => { setFreetextReference(e.target.value); }}
                      placeholder="e.g. a link or note"
                    />
                  </div>
                  <Button
                    disabled={busy || freetextTitle.trim().length === 0}
                    onClick={() => {
                      const title = freetextTitle.trim();
                      const reference = freetextReference.trim();
                      void run(() =>
                        addFreetextToSlot(weekStart, date, mealType, title, reference || undefined),
                      );
                    }}
                  >
                    Add dish
                  </Button>
                </div>
              )}

              {mode === "leftover" && (
                <div className="flex flex-col gap-3">
                  <BackButton onClick={() => { setMode("menu"); }} />
                  {leftoverCandidates.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs uppercase tracking-wide text-zinc-500">This week</p>
                      {leftoverCandidates.map((c) => (
                        <button
                          key={`${c.date}#${c.slot.mealType}`}
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            void run(() =>
                              setSlotLeftoverFromPosition(weekStart, date, mealType, c.date, c.slot.mealType),
                            );
                          }}
                          className="rounded-md px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                        >
                          {c.date} &middot; {MEAL_LABELS[c.slot.mealType]} &mdash;{" "}
                          {c.slot.items.map((item) => resolveItemLabel(item, recipeTitles)).join(", ")}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="slot-leftover-label">Source (typed)</Label>
                    <Input
                      id="slot-leftover-label"
                      value={leftoverLabel}
                      onChange={(e) => { setLeftoverLabel(e.target.value); }}
                      placeholder="e.g. Last week's roast"
                    />
                  </div>
                  <Button
                    disabled={busy || leftoverLabel.trim().length === 0}
                    onClick={() => {
                      const label = leftoverLabel.trim();
                      void run(() => setSlotLeftover(weekStart, date, mealType, { label }));
                    }}
                  >
                    Set
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              {mode === "menu" && (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-zinc-200">
                    {slot.leftoverOf
                      ? `Leftovers${slot.leftoverOf.label ? ` from ${slot.leftoverOf.label}` : ""}`
                      : slot.items.map((item) => resolveItemLabel(item, recipeTitles)).join(", ")}
                  </p>
                  <div className="flex flex-col gap-2 border-t border-zinc-800 pt-3">
                    <Button variant="outline" className="justify-start" onClick={() => { setMode("move"); }}>
                      Move this dish to&hellip;
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={() => { setMode("copy"); }}>
                      Copy this dish to&hellip;
                    </Button>
                    {confirmingRemove ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-400">Remove this dish?</span>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            setConfirmingRemove(false);
                            void run(() => removeSlotItems(weekStart, date, mealType));
                          }}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => { setConfirmingRemove(false); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        className="justify-start text-zinc-500 hover:text-destructive"
                        onClick={() => { setConfirmingRemove(true); }}
                      >
                        Remove dish
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {mode === "move" && (
                <div className="flex flex-col gap-1 overflow-y-auto">
                  <BackButton onClick={() => { setMode("menu"); }} />
                  {moveTargets.map((t) => (
                    <button
                      key={`${t.date}#${t.mealType}`}
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        void run(() => moveSlotItems(weekStart, date, mealType, t.date, t.mealType));
                      }}
                      className="rounded-md px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                    >
                      {t.date} &middot; {MEAL_LABELS[t.mealType]}
                    </button>
                  ))}
                </div>
              )}

              {mode === "copy" && (
                <div className="flex flex-col gap-3">
                  <BackButton onClick={() => { setMode("menu"); }} />
                  <div className="flex flex-col gap-1 overflow-y-auto">
                    {moveTargets.map((t) => {
                      const key = `${t.date}#${t.mealType}`;
                      return (
                        <label
                          key={key}
                          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800"
                        >
                          <input
                            type="checkbox"
                            checked={copyTargets.has(key)}
                            onChange={() => { toggleCopyTarget(key); }}
                          />
                          {t.date} &middot; {MEAL_LABELS[t.mealType]}
                        </label>
                      );
                    })}
                  </div>
                  <Button
                    disabled={busy || copyTargets.size === 0}
                    onClick={() => {
                      const targets = [...copyTargets].map((key) => {
                        const [targetDate, targetMealType] = key.split("#") as [string, MealType];
                        return { date: targetDate, mealType: targetMealType };
                      });
                      void run(() => copySlotItemsToTargets(weekStart, date, mealType, targets));
                    }}
                  >
                    Copy to {copyTargets.size} {copyTargets.size === 1 ? "slot" : "slots"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BackButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <Button variant="ghost" size="sm" className="w-fit" onClick={onClick}>
      &larr; Back
    </Button>
  );
}
